import React, { useCallback, useEffect, useState } from "react";
import {
  Alert, Box, Button, Chip, CircularProgress, IconButton, InputAdornment, Paper, Snackbar, Table, TableBody, TableCell, TableContainer, TableHead, TablePagination,
  TableRow, TextField, Tooltip, Typography,
} from "@mui/material";
import AddIcon from "@mui/icons-material/Add";
import AttachFileIcon from "@mui/icons-material/AttachFile";
import ListAltIcon from "@mui/icons-material/ListAlt";
import ReceiptLongIcon from "@mui/icons-material/ReceiptLongOutlined";
import SearchIcon from "@mui/icons-material/Search";
import ShoppingCartIcon from "@mui/icons-material/ShoppingCartOutlined";
import VisibilityIcon from "@mui/icons-material/VisibilityOutlined";
import DashboardLayout from "../../../layouts/DashboardLayout";
import PageHeader from "../../../components/layout/PageHeader";
import { useAuth } from "../../../context/AuthContext";
import SolicitudFormDialog from "../components/SolicitudFormDialog";
import SolicitudMasivaDialog from "../components/SolicitudMasivaDialog";
import SolicitudDetalleDialog from "../components/SolicitudDetalleDialog";
import { ESTADOS, fmtMonto, getCatalogosSolicitud, getResumenSolicitudes, listarSolicitudes, mensajeError } from "../services/solicitudChequeService";

const headSx = { fontWeight: 800, whiteSpace: "nowrap", fontSize: 11.5, color: "text.secondary", textTransform: "uppercase", letterSpacing: 0.4, py: 0.5, px: 1 };
// Filas de una sola línea: el texto largo se corta con "…" y se ve completo al pasar el mouse
const cellSx = { py: 0.25, px: 1, fontSize: 12.5, verticalAlign: "middle", whiteSpace: "nowrap" };
const cortoSx = { overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" };

const FILTROS = [
  { clave: "", texto: "Todas", color: "default" },
  { clave: "espera", texto: "En espera", color: "warning" },
  { clave: "autorizado", texto: "Autorizadas", color: "info" },
  { clave: "emitido", texto: "Emitidas", color: "success" },
];

// Solicitud de cheque: cada usuario pide un cheque a Contabilidad y sigue el estado de SUS solicitudes.
export default function SolicitudChequePage() {
  const { user } = useAuth();
  const puedeAdministrar = ["admin", "gerente", "conta"].includes(user?.role);
  const [catalogos, setCatalogos] = useState({});
  const [resumen, setResumen] = useState({ total: 0, espera: 0, autorizado: 0, emitido: 0 });
  const [filas, setFilas] = useState([]);
  const [total, setTotal] = useState(0);
  const [cargando, setCargando] = useState(true);
  const [error, setError] = useState("");
  const [aviso, setAviso] = useState({ texto: "", severidad: "success" });

  const [buscar, setBuscar] = useState("");
  const [q, setQ] = useState("");
  const [estado, setEstado] = useState("");
  const [pagina, setPagina] = useState(0);
  const [limite, setLimite] = useState(10);

  const [formAbierto, setFormAbierto] = useState(null); // null | "normal" | "oc"
  const [masivaAbierta, setMasivaAbierta] = useState(false);
  const [detalleId, setDetalleId] = useState(null);

  useEffect(() => {
    getCatalogosSolicitud().then(({ data }) => setCatalogos(data)).catch((e) => setError(mensajeError(e, "No se pudieron cargar los datos del formulario.")));
  }, []);

  useEffect(() => {
    const t = setTimeout(() => { setQ(buscar.trim()); setPagina(0); }, 300);
    return () => clearTimeout(t);
  }, [buscar]);

  const cargar = useCallback(async () => {
    setCargando(true);
    try {
      const [lista, res] = await Promise.all([
        listarSolicitudes({ q: q || undefined, estado: estado || undefined, page: pagina + 1, limit: limite }),
        getResumenSolicitudes(),
      ]);
      setFilas(lista.data.rows);
      setTotal(lista.data.total);
      setResumen(res.data);
      setError("");
    } catch (e) {
      setError(mensajeError(e, "No se pudieron cargar las solicitudes."));
    } finally {
      setCargando(false);
    }
  }, [q, estado, pagina, limite]);

  useEffect(() => { cargar(); }, [cargar]);

  const volverAlInicio = () => { setBuscar(""); setQ(""); setEstado(""); setPagina(0); };
  const alGuardar = ({ id, aviso: problema }) => {
    setFormAbierto(null);
    setAviso(problema ? { texto: problema, severidad: "warning" } : { texto: `Solicitud ${id} enviada a Contabilidad.`, severidad: "success" });
    volverAlInicio();
    cargar();
  };
  const listoParaCrear = !!catalogos.conceptos;

  return (
    <DashboardLayout>
      <PageHeader
        title="Solicitud de cheque"
        subtitle="Pide un cheque a Contabilidad y sigue el estado de tus solicitudes"
        actions={(
          <Box sx={{ display: "flex", gap: 1, flexWrap: "wrap" }}>
            <Button variant="outlined" size="small" startIcon={<ShoppingCartIcon />} onClick={() => setFormAbierto("oc")} disabled={!listoParaCrear} sx={{ textTransform: "none" }}>Orden de compra</Button>
            <Button variant="outlined" size="small" startIcon={<ListAltIcon />} onClick={() => setMasivaAbierta(true)} disabled={!listoParaCrear} sx={{ textTransform: "none" }}>Solicitud masiva</Button>
            <Button variant="contained" size="small" startIcon={<AddIcon />} onClick={() => setFormAbierto("normal")} disabled={!listoParaCrear} sx={{ textTransform: "none", fontWeight: 700 }}>Nueva solicitud</Button>
          </Box>
        )}
      />

      <Box sx={{ p: { xs: 1.5, md: 2 }, display: "grid", gap: 1.5 }}>
        <Box sx={{ display: "flex", gap: 1.5, flexWrap: "wrap", alignItems: "center", justifyContent: "space-between" }}>
          <Box sx={{ display: "flex", gap: 0.75, flexWrap: "wrap" }} role="group" aria-label="Filtrar por estado">
            {FILTROS.map((f) => {
              const cuenta = f.clave ? resumen[f.clave] : resumen.total;
              const activo = estado === f.clave;
              return (
                <Chip
                  key={f.clave || "todas"} label={`${f.texto} · ${cuenta ?? 0}`} onClick={() => { setEstado(f.clave); setPagina(0); }}
                  color={activo ? (f.color === "default" ? "primary" : f.color) : "default"} variant={activo ? "filled" : "outlined"}
                  sx={{ fontWeight: 700, fontSize: 12.5 }} aria-pressed={activo}
                />
              );
            })}
          </Box>
          <Box sx={{ display: "flex", alignItems: "center", gap: 1, width: { xs: "100%", sm: "auto" } }}>
            {cargando && <CircularProgress size={18} />}
            <TextField
              size="small" placeholder="Buscar por código, destinatario o concepto…" value={buscar} onChange={(e) => setBuscar(e.target.value)} sx={{ width: { xs: "100%", sm: 340 } }}
              slotProps={{ input: { startAdornment: <InputAdornment position="start"><SearchIcon fontSize="small" /></InputAdornment> } }}
            />
          </Box>
        </Box>

        {error && <Alert severity="error" onClose={() => setError("")}>{error}</Alert>}

        <Paper variant="outlined">
          <TableContainer sx={{ overflowX: "auto" }}>
            <Table size="small">
              <TableHead>
                <TableRow sx={{ bgcolor: "action.hover" }}>
                  <TableCell sx={headSx}>N.º</TableCell>
                  <TableCell sx={headSx}>Fecha</TableCell>
                  <TableCell sx={headSx}>Dirigido a / Concepto</TableCell>
                  <TableCell sx={headSx}>Servicio</TableCell>
                  <TableCell sx={headSx} align="right">Monto</TableCell>
                  <TableCell sx={headSx}>Estado</TableCell>
                  <TableCell sx={headSx} align="center">Adjuntos</TableCell>
                  <TableCell sx={headSx} align="center">Detalle</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {!filas.length ? (
                  <TableRow>
                    <TableCell colSpan={8} sx={{ py: 5, textAlign: "center" }}>
                      {cargando ? (
                        <Typography variant="body2" color="text.secondary">Cargando…</Typography>
                      ) : (
                        <Box sx={{ display: "grid", justifyItems: "center", gap: 1 }}>
                          <ReceiptLongIcon sx={{ fontSize: 40, color: "text.disabled" }} />
                          <Typography sx={{ fontWeight: 700 }}>{q || estado ? "No hay solicitudes con ese filtro" : "Aún no tienes solicitudes"}</Typography>
                          {q || estado ? (
                            <Button size="small" onClick={volverAlInicio} sx={{ textTransform: "none" }}>Quitar filtros</Button>
                          ) : (
                            <Button size="small" variant="contained" startIcon={<AddIcon />} onClick={() => setFormAbierto("normal")} disabled={!listoParaCrear} sx={{ textTransform: "none" }}>Crear mi primera solicitud</Button>
                          )}
                        </Box>
                      )}
                    </TableCell>
                  </TableRow>
                ) : filas.map((s) => {
                  const est = ESTADOS[s.estado];
                  return (
                    <TableRow key={s.id} hover onClick={() => setDetalleId(s.id)} sx={{ cursor: "pointer" }}>
                      <TableCell sx={{ ...cellSx, fontWeight: 800 }}>{s.id}</TableCell>
                      <TableCell sx={{ ...cellSx, whiteSpace: "nowrap" }}>{String(s.fecha || "").slice(0, 10)}</TableCell>
                      <TableCell sx={{ ...cellSx, maxWidth: 0, width: "45%", minWidth: 220 }}>
                        <Tooltip title={[s.nombre, s.concepto].filter(Boolean).join(" — ")} enterDelay={500}>
                          <Box sx={cortoSx}>
                            <Box component="span" sx={{ fontWeight: 700 }}>{s.nombre}</Box>
                            {s.concepto && <Box component="span" sx={{ color: "text.secondary" }}>{` — ${s.concepto}`}</Box>}
                          </Box>
                        </Tooltip>
                      </TableCell>
                      <TableCell sx={cellSx}>
                        {s.servicio}{s.mes && <Box component="span" sx={{ color: "text.secondary" }}>{` · ${s.mes}`}</Box>}
                      </TableCell>
                      <TableCell sx={{ ...cellSx, fontVariantNumeric: "tabular-nums" }} align="right">
                        <Box component="span" sx={{ fontWeight: 700 }}>Bs {fmtMonto(s.bolivianos)}</Box>
                        <Box component="span" sx={{ color: "text.secondary", fontSize: 11.5 }}>{`  ·  $us ${fmtMonto(s.dolares)}`}</Box>
                      </TableCell>
                      <TableCell sx={cellSx}>{est ? <Chip size="small" label={est.texto} color={est.color} sx={{ fontWeight: 700, height: 20, fontSize: 11.5 }} /> : s.estado}</TableCell>
                      <TableCell sx={cellSx} align="center">
                        <Box sx={{ display: "inline-flex", gap: 1, color: "text.secondary", alignItems: "center" }}>
                          {s.total_archivos > 0 && <Tooltip title={`${s.total_archivos} archivo(s)`}><Box sx={{ display: "inline-flex", alignItems: "center", gap: 0.25, fontSize: 12 }}><AttachFileIcon sx={{ fontSize: 16 }} />{s.total_archivos}</Box></Tooltip>}
                          {s.total_facturas > 0 && <Tooltip title={`${s.total_facturas} factura(s)`}><Box sx={{ display: "inline-flex", alignItems: "center", gap: 0.25, fontSize: 12 }}><ReceiptLongIcon sx={{ fontSize: 16 }} />{s.total_facturas}</Box></Tooltip>}
                          {!s.total_archivos && !s.total_facturas && "—"}
                        </Box>
                      </TableCell>
                      <TableCell sx={cellSx} align="center">
                        <Tooltip title="Ver detalle"><IconButton size="small" sx={{ p: 0.25 }} aria-label={`Ver solicitud ${s.id}`} onClick={(e) => { e.stopPropagation(); setDetalleId(s.id); }}><VisibilityIcon fontSize="small" /></IconButton></Tooltip>
                      </TableCell>
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
          </TableContainer>
          <TablePagination
            component="div" count={total} page={pagina} rowsPerPage={limite} rowsPerPageOptions={[10, 25, 50]}
            onPageChange={(_, p) => setPagina(p)} onRowsPerPageChange={(e) => { setLimite(Number(e.target.value)); setPagina(0); }}
            labelRowsPerPage="Filas" labelDisplayedRows={({ from, to, count }) => `${from}-${to} de ${count}`}
          />
        </Paper>
      </Box>

      <SolicitudFormDialog open={!!formAbierto} modo={formAbierto || "normal"} catalogos={catalogos} onClose={() => setFormAbierto(null)} onGuardada={alGuardar} />
      <SolicitudMasivaDialog
        open={masivaAbierta} catalogos={catalogos} puedeAdministrar={puedeAdministrar} onClose={() => setMasivaAbierta(false)}
        onEnviadas={(n) => { setMasivaAbierta(false); setAviso({ texto: `${n} solicitud(es) enviada(s) a Contabilidad.`, severidad: "success" }); volverAlInicio(); cargar(); }}
      />
      <SolicitudDetalleDialog id={detalleId} onClose={() => setDetalleId(null)} onFacturasAgregadas={cargar} />

      <Snackbar open={!!aviso.texto} autoHideDuration={5000} onClose={() => setAviso((a) => ({ ...a, texto: "" }))} anchorOrigin={{ vertical: "bottom", horizontal: "center" }}>
        <Alert severity={aviso.severidad} variant="filled" onClose={() => setAviso((a) => ({ ...a, texto: "" }))}>{aviso.texto}</Alert>
      </Snackbar>
    </DashboardLayout>
  );
}
